/**
 * ErrorBoundary Component
 *
 * Catches JavaScript errors anywhere in the component tree and displays
 * a fallback UI instead of crashing the entire app.
 *
 * Usage:
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 */

import React from 'react';
import PropTypes from 'prop-types';
import Icon from './Icon';
import { recordTelemetryEvent } from '../../services/telemetry';
import './ErrorBoundary.css';

const ERROR_BOUNDARY_HITS_KEY = 'thryveloop_error_boundary_hits_v1';
const ERROR_BOUNDARY_EVENTS_KEY = 'thryveloop_error_boundary_events_v1';
const ERROR_BOUNDARY_EVENT_RETENTION_MS = 72 * 60 * 60 * 1000;

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
    };
    this.chunkReloadKey = 'thryveloop_chunk_reload';
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    if (this.maybeRecoverFromChunkError(error)) {
      return;
    }

    // Update state with error details
    this.setState(prevState => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1,
    }));

    try {
      if (typeof window !== 'undefined') {
        const now = Date.now();
        const raw = window.localStorage.getItem(ERROR_BOUNDARY_EVENTS_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        const recentEvents = (Array.isArray(parsed) ? parsed : [])
          .map((value) => Number(value))
          .filter((value) => Number.isFinite(value) && value >= (now - ERROR_BOUNDARY_EVENT_RETENTION_MS) && value <= (now + 60 * 1000));
        recentEvents.push(now);
        // Cap retained entries to avoid unbounded localStorage growth.
        const cappedEvents = recentEvents.slice(-120);
        window.localStorage.setItem(ERROR_BOUNDARY_EVENTS_KEY, JSON.stringify(cappedEvents));
        window.localStorage.setItem(ERROR_BOUNDARY_HITS_KEY, String(cappedEvents.length));
      }
    } catch (_) {}

    recordTelemetryEvent('error_boundary_hit', {
      level: this.props.level || 'section',
      message: error?.message || String(error || 'unknown_error'),
      componentStack: errorInfo?.componentStack || null
    }).catch(() => {});

    // TODO: Log to error reporting service (e.g., Sentry)
    // logErrorToService(error, errorInfo);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  maybeRecoverFromChunkError(error) {
    const message = error?.message || '';
    const lowerMessage = message.toLowerCase();
    const isChunkError =
      error?.name === 'ChunkLoadError' ||
      message.includes('Loading CSS chunk') ||
      message.includes('Loading chunk') ||
      message.includes('Failed to fetch dynamically imported module') ||
      lowerMessage.includes('error loading dynamically imported module') ||
      message.includes('Importing a module script failed');

    if (!isChunkError || typeof window === 'undefined') {
      return false;
    }

    try {
      const lastAttempt = Number(window.sessionStorage.getItem(this.chunkReloadKey) || '0');
      const now = Date.now();

      // If we haven't retried recently (5 minutes), reload automatically
      if (!lastAttempt || now - lastAttempt > 5 * 60 * 1000) {
        window.sessionStorage.setItem(this.chunkReloadKey, String(now));
        console.warn('ChunkLoadError detected, reloading application to fetch fresh bundles.');
        window.location.reload();
        return true;
      }

      // Already retried recently - clear flag so future deployments can recover
      window.sessionStorage.removeItem(this.chunkReloadKey);
      return false;
    } catch (storageError) {
      console.warn('Unable to persist chunk reload attempt:', storageError);
      window.location.reload();
      return true;
    }
  }

  render() {
    const { hasError, error, errorInfo, errorCount } = this.state;
    const { children, fallback, level = 'section' } = this.props;

    if (hasError) {
      // Use custom fallback if provided
      if (fallback) {
        return typeof fallback === 'function'
          ? fallback(error, this.handleReset)
          : fallback;
      }

      // Default fallback UI
      return (
        <div className={`error-boundary error-boundary-${level}`}>
          <div className="error-boundary-content">
            <div className="error-boundary-icon" aria-hidden="true">
              <Icon name="alert-triangle" size={38} strokeWidth={2.1} />
            </div>
            <h2 className="error-boundary-title">
              {level === 'app' ? 'Application Error' : 'Something went wrong'}
            </h2>
            <p className="error-boundary-message">
              {level === 'app'
                ? 'We encountered an unexpected error. Please try refreshing the page.'
                : 'This section encountered an error, but the rest of the app should work fine.'}
            </p>
            {error?.message && (
              <p className="error-boundary-detail-line" style={{ color: 'var(--error-text)', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                <strong>Details:</strong> {error.message}
              </p>
            )}

            {process.env.NODE_ENV === 'development' && error && (
              <details className="error-boundary-details">
                <summary>Error Details (Development Only)</summary>
                <div className="error-boundary-stack">
                  <p>
                    <strong>Error:</strong> {error.toString()}
                  </p>
                  {errorInfo && (
                    <pre>{errorInfo.componentStack}</pre>
                  )}
                </div>
              </details>
            )}

            <div className="error-boundary-actions">
              <button
                className="btn-modern primary"
                onClick={this.handleReset}
              >
                Try Again
              </button>
              {level === 'app' && (
                <button
                  className="btn-modern secondary"
                  onClick={this.handleReload}
                >
                  Reload Page
                </button>
              )}
            </div>

            {errorCount > 3 && (
              <p className="error-boundary-warning">
                This error has occurred {errorCount} times. You may want to reload the page.
              </p>
            )}
          </div>
        </div>
      );
    }

    return children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  level: PropTypes.oneOf(['app', 'section']),
};

ErrorBoundary.defaultProps = {
  fallback: null,
  level: 'section',
};

export default ErrorBoundary;
